/**
 * 문항 유형 → 채움 슬롯 스키마. "어느 유형의 어느 배열이 슬롯인가 · 폼 어디에 쓰나 ·
 * 비어 있어도 보호하는 자리는 어디인가" 를 유형별 한 행으로 선언한다.
 *
 * 파이프라인(채움 적용 · 리비전 · 자격 · 정답 지목 · 폼 쓰기)은 유형을 분기하지 않고
 * `slotsOf` 로 정규화된 슬롯만 본다. 새 유형 지원 = 이 테이블에 행 하나.
 *
 * 슬롯 id 는 폼의 `key`(nanoid)다. 필드배열이 재정렬·삽입·삭제돼도 유지되므로, 응답이
 * 돌아왔을 때 "그때 그 자리" 를 인덱스보다 정확하게 가리킨다.
 */
import { type Path } from 'react-hook-form';

import { type BuildSchemaInputType } from '../../../form/schema';
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { type FillSlotVariant } from '../types/questionCardAi.types';

export type Slot = {
  key: string;
  /** trim 전 원문. */
  text: string;
  imageUrl: string | null;
  isAnswer: boolean;
};

type FormPath = Path<BuildSchemaInputType>;

type SlotSchema = {
  /** 문항 → 정규화된 슬롯 목록(폼 순서 그대로). */
  read: (quiz: BuildQuizListItem) => Slot[];
  /** 슬롯 index 의 폼 필드 경로. richText·isAnswer 는 그 필드가 있는 유형만. */
  path: (
    quizIndex: number,
    slotIndex: number,
  ) => { text: FormPath; richText?: FormPath; isAnswer?: FormPath };
  /** 비어 있어도 사용자 의도로 보는 자리 — 리비전 보호 대상이다. */
  pinned: (slot: Slot, index: number) => boolean;
  /** 정답이 없던 요청에서 AI 가 지목한 정답 자리를 받아 찍을 수 있나. */
  acceptsAnswerPick: boolean;
  /** 카피 변형 — 객관식은 "선택지", 단답형은 "답안". */
  variant: FillSlotVariant;
};

export const QUIZ_SLOT_SCHEMA = {
  multiple: {
    read: quiz =>
      quiz.multipleData.choiceList.map(choice => ({
        key: choice.key,
        text: choice.text ?? '',
        imageUrl: choice.imageUrl || null,
        isAnswer: choice.isAnswer,
      })),
    path: (quizIndex, slotIndex) => ({
      text: `quizList.${quizIndex}.multipleData.choiceList.${slotIndex}.text`,
      richText: `quizList.${quizIndex}.multipleData.choiceList.${slotIndex}.richText`,
      isAnswer: `quizList.${quizIndex}.multipleData.choiceList.${slotIndex}.isAnswer`,
    }),
    // 비어 있어도 정답 자리는 보호한다 — 빈 0번에서 빈 1번으로 정답을 옮긴 것도 변경이다.
    pinned: slot => slot.isAnswer,
    acceptsAnswerPick: true,
    variant: 'choice',
  },
  subjective: {
    read: quiz =>
      quiz.subjectiveData.answerList.map(answer => ({
        key: answer.key,
        text: answer.text ?? '',
        imageUrl: null,
        isAnswer: false,
      })),
    path: (quizIndex, slotIndex) => ({
      text: `quizList.${quizIndex}.subjectiveData.answerList.${slotIndex}.text`,
    }),
    // 0번은 주요 정답이라 비어 있어도 보호한다.
    pinned: (_, index) => index === 0,
    acceptsAnswerPick: false,
    variant: 'answer',
  },
} satisfies Partial<Record<BuildQuizListItem['type'], SlotSchema>>;

export type SlotQuizType = keyof typeof QUIZ_SLOT_SCHEMA;

/** 슬롯 스키마가 있는 유형 = 선지 채움이 다루는 유형. */
export const SLOT_QUIZ_TYPES = Object.keys(
  QUIZ_SLOT_SCHEMA,
) as readonly SlotQuizType[];

export const slotSchemaOf = (
  quizType: BuildQuizListItem['type'] | string,
): SlotSchema | null =>
  (QUIZ_SLOT_SCHEMA as Partial<Record<string, SlotSchema>>)[quizType] ?? null;

export const slotsOf = (quiz: BuildQuizListItem): Slot[] =>
  slotSchemaOf(quiz.type)?.read(quiz) ?? [];

/** 이미지만 있는 선지는 "채워진" 것으로 본다 — 서버에도 자리표시 텍스트로 나간다. */
export const isSlotFilled = (slot: Pick<Slot, 'text' | 'imageUrl'>) =>
  !!slot.text.trim() || !!slot.imageUrl;

export const slotIdsOf = (quiz: BuildQuizListItem): string[] =>
  slotsOf(quiz).map(slot => slot.key);

export const fillSlotVariantOf = (
  quizType: BuildQuizListItem['type'] | string,
): FillSlotVariant => slotSchemaOf(quizType)?.variant ?? 'choice';
